import { ToolIcon } from '@/components/ToolIcon';
import { cn } from '@/lib/cn';
import { say } from '@/lib/vocabulary';

/**
 * How each tool's folder is organised inside the package.
 *
 * The backend offers a real choice per tool - group by the container the
 * files came from, by what kind of thing they are, or by when - and tells
 * you how many folders each choice produces and which it recommends. That
 * was rendered as a raw dump behind a disclosure, so nobody could have used
 * it. It is a decision about what the buyer opens, which makes it worth
 * showing.
 *
 * A choice with one group is offered but marked, because collapsing a tool
 * into a single folder is sometimes right and usually not.
 */

export interface LayoutOption {
  level3: string;
  groupCount: number;
  recommended: boolean;
}

export interface ToolLayout {
  toolId: string;
  groupPath: string;
  currentLevel3: string;
  currentLeafOrder: string;
  options: LayoutOption[];
}

export interface Layouts {
  tools: ToolLayout[];
  leafOrders: string[];
}

const GROUPING: Record<string, string> = {
  NATIVE_CONTAINER: 'the folder it came from',
  ENTITY_CLASS: 'what kind of thing it is',
  TIME_PERIOD: 'when it was made',
  // "a second axis" is the schema's word for it, and it reached the screen
  // unchanged in a list where every other option is a plain description of
  // what the buyer will see.
  SECONDARY_AXIS: 'the second attribute this package is organised by',
};

const ORDER: Record<string, string> = {
  CHRONOLOGICAL: 'oldest first',
  ALPHABETICAL: 'by name',
  NATIVE_ORDER: 'as the tool had it',
};

export function readLayouts(value: unknown): Layouts | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (!Array.isArray(v.tools)) return null;
  return {
    leafOrders: Array.isArray(v.leafOrders) ? (v.leafOrders as string[]) : [],
    tools: (v.tools as Record<string, unknown>[]).map((t) => ({
      toolId: String(t.toolId ?? ''),
      groupPath: String(t.groupPath ?? ''),
      currentLevel3: String(t.currentLevel3 ?? ''),
      currentLeafOrder: String(t.currentLeafOrder ?? ''),
      options: Array.isArray(t.options)
        ? (t.options as Record<string, unknown>[]).map((o) => ({
            level3: String(o.level3 ?? ''),
            groupCount: typeof o.groupCount === 'number' ? o.groupCount : 0,
            recommended: o.recommended === true,
          }))
        : [],
    })),
  };
}

export function FolderLayout({
  layouts,
  busy,
  onChoose,
}: {
  layouts: Layouts;
  busy: string | null;
  onChoose: (groupPath: string, level3: string, leafOrder: string) => void;
}) {
  return (
    <div>
      {layouts.tools.map((t) => (
        <div key={t.toolId} className="border-b border-line px-4 py-3 last:border-0">
          <div className="flex items-center gap-2.5">
            <ToolIcon tool={t.toolId} size={16} />
            <span className="text-small text-ink">{t.toolId.replace(/_/g, ' ')}</span>
            <span className="ml-auto text-caption text-ink-4">
              inside, sort {ORDER[t.currentLeafOrder] ?? t.currentLeafOrder.toLowerCase()}
            </span>
          </div>

          <div className="mt-2 flex flex-wrap gap-1.5">
            {t.options.map((o) => {
              const chosen = o.level3 === t.currentLevel3;
              const key = `${t.groupPath}:${o.level3}`;
              return (
                <button
                  key={o.level3}
                  aria-pressed={chosen}
                  disabled={busy !== null}
                  onClick={() => onChoose(t.groupPath, o.level3, t.currentLeafOrder)}
                  className={cn(
                    'rounded border px-2.5 py-1 text-caption transition-colors duration-fast disabled:opacity-50',
                    chosen
                      ? 'border-acc bg-acc-soft text-acc-2'
                      // Unchosen options were drawn at the weight of disabled
                      // text, and raising them to full ink overshot: measured,
                      // they became the brightest text in the row at 14.9:1
                      // against the live choice at 9.8:1, so the two options
                      // the buyer will not get read as the emphasised ones.
                      // ink-2 is legible and stays subordinate.
                      : 'border-line-2 bg-panel-2 text-ink-2 hover:bg-hov hover:text-ink',
                  )}
                >
                  {busy === key ? 'saving…' : `group by ${GROUPING[o.level3] ?? say(o.level3)}`}
                  <span className="ml-1.5 text-ink-4">
                    {o.groupCount === 1 ? '1 folder' : `${o.groupCount} folders`}
                  </span>
                  {o.recommended && <span className="ml-1.5 text-ok">suggested</span>}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
